import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ConnectionChoice } from '@app/types/sdk';
import type { DiscoveryMembership } from '@app/types/auth';
import { useAuthFlow } from '@app/context/auth-flow';

const allowsLocalAuth = (membership?: DiscoveryMembership): boolean => {
  if (!membership) {
    return false;
  }
  if (membership.policy?.requiresSso) {
    return false;
  }
  return membership.status === 'active' || membership.status === 'invited';
};

const OrgSelectPage = () => {
  const { discovery, selectedMembership, selectMembership } = useAuthFlow();
  const navigate = useNavigate();

  useEffect(() => {
    if (discovery.status !== 'verified') {
      navigate('/login');
      return;
    }
  }, [discovery.memberships.length, discovery.status, navigate]);

  useEffect(() => {
    if (discovery.status !== 'verified') {
      return;
    }
    if (selectedMembership) {
      return;
    }
    const firstEligible =
      discovery.memberships.find((membership) => allowsLocalAuth(membership)) ??
      discovery.memberships.find((membership) => membership.status === 'active');
    if (firstEligible) {
      selectMembership(firstEligible);
    }
  }, [discovery.memberships, discovery.status, selectMembership, selectedMembership]);

  const handlePasskeyAction = (membership: DiscoveryMembership) => {
    selectMembership(membership);
    if (membership.status === 'active') {
      navigate(`/passkeys/sign-in?orgId=${membership.orgId}`);
    } else {
      navigate(`/passkeys/register?orgId=${membership.orgId}`);
    }
  };

  const handlePasswordAction = (membership: DiscoveryMembership) => {
    selectMembership(membership);
    if (membership.status === 'active') {
      navigate(`/password/sign-in?orgId=${membership.orgId}`);
    } else {
      navigate(`/password/register?orgId=${membership.orgId}`);
    }
  };

  const handleSso = (membership: DiscoveryMembership, connection: ConnectionChoice) => {
    selectMembership(membership);
    if (connection.startUrl) {
      const params = new URLSearchParams({
        target: connection.startUrl,
        orgId: membership.orgId,
        name: connection.name
      });
      navigate(`/sso/redirect?${params.toString()}`, {
        state: {
          startUrl: connection.startUrl,
          orgId: membership.orgId,
          connectionName: connection.name
        }
      });
    }
  };

  const goToOrgCreate = () => {
    navigate('/orgs/create');
  };

  const memberships = discovery.memberships ?? [];
  const localConnections = discovery.localConnections ?? [];
  const hasPasskey = localConnections.includes('passkey');
  const hasPassword = localConnections.includes('password');
  const localEligibleMemberships = memberships.filter((membership) => allowsLocalAuth(membership));
  const localDisabledMessage = (() => {
    if (memberships.length === 0) {
      return null;
    }
    if (localEligibleMemberships.length === 0) {
      return 'All organizations require SSO. Use SSO or contact an administrator.';
    }
    if (!selectedMembership) {
      return 'Select an organization to use passkey or password.';
    }
    if (selectedMembership.policy?.requiresSso) {
      return `${selectedMembership.orgName} requires SSO. Choose another organization to use local methods.`;
    }
    return null;
  })();

  return (
    <main className="auth-layout">
      <section className="auth-card wide">
        <header className="auth-card__header">
          <p className="auth-step">Step 3 of 3</p>
          <h1 className="auth-title">Choose how to continue</h1>
          <p className="auth-subtitle">
            Verified for <strong>{discovery.email}</strong>. Pick an organization and sign-in method.
          </p>
        </header>

        {(hasPasskey || hasPassword) && (
          <section className="local-options">
            <h2 className="local-options__title">Local methods</h2>
            <p className="local-options__subtitle">Use passkeys or passwords linked to your email.</p>
            <div className="local-options__actions">
              {hasPasskey && (
                <button
                  type="button"
                  className="auth-submit secondary"
                  disabled={Boolean(localDisabledMessage) && memberships.length > 0}
                  onClick={() => {
                    if (memberships.length === 0) {
                      navigate('/passkeys/register');
                      return;
                    }
                    const target = selectedMembership;
                    if (!target || !allowsLocalAuth(target)) {
                      return;
                    }
                    handlePasskeyAction(target);
                  }}
                >
                  {selectedMembership?.status === 'active' ? 'Continue with passkey' : 'Set up passkey'}
                </button>
              )}
              {hasPassword && (
                <button
                  type="button"
                  className="auth-submit secondary"
                  disabled={Boolean(localDisabledMessage) && memberships.length > 0}
                  onClick={() => {
                    if (memberships.length === 0) {
                      navigate('/password/register');
                      return;
                    }
                    const target = selectedMembership;
                    if (!target || !allowsLocalAuth(target)) {
                      return;
                    }
                    handlePasswordAction(target);
                  }}
                >
                  {selectedMembership?.status === 'active' ? 'Continue with password' : 'Set up password'}
                </button>
              )}
            </div>
            {localDisabledMessage && memberships.length > 0 && <p className="auth-note">{localDisabledMessage}</p>}
          </section>
        )}

        <div className="org-options">
          {memberships.map((membership) => {
            const isSelected = selectedMembership?.orgId === membership.orgId;
            const connections: ConnectionChoice[] = membership.connections ?? [];
            const requiresSso = membership.policy?.requiresSso === true;
            const isActive = membership.status === 'active';

            return (
              <article
                key={membership.orgId}
                className={`org-option ${isSelected ? 'selected' : ''}`}
                tabIndex={0}
                role="button"
                aria-pressed={isSelected}
                onClick={() => selectMembership(membership)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
                    event.preventDefault();
                    selectMembership(membership);
                  }
                }}
              >
                <div className="org-option__header">
                  <div className="org-option__title">
                    <h2>
                      {membership.orgName}{' '}
                      <span className="org-option__id">({membership.orgSlug ?? membership.orgId})</span>
                    </h2>
                  </div>
                  <span className={`status-chip status-chip--${membership.status}`}>{membership.status}</span>
                </div>

                {membership.note && <p className="org-note">{membership.note}</p>}

                {requiresSso && (
                  <p className="org-note org-note--warning">Local authentication disabled by policy. Use SSO.</p>
                )}

                {connections.length > 0 ? (
                  <ul className="org-option__connections">
                    {connections.map((connection: ConnectionChoice) => {
                      const spInitiated = Boolean(connection.startUrl) && connection.idpInitiated !== true;
                      return (
                        <li key={`${membership.orgId}-${connection.name}`} className="org-option__connection">
                          <div className="org-option__connection-meta">
                            <span className="org-option__connection-name">{connection.name}</span>
                            <span className={`org-option__type org-option__type--${connection.type}`}>
                              {connection.type.toUpperCase()}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="auth-submit secondary sso-button"
                            onClick={() => {
                              if (spInitiated) {
                                handleSso(membership, connection);
                              }
                            }}
                            disabled={!spInitiated}
                            title={
                              spInitiated
                                ? `Continue with ${connection.name}`
                                : 'Start from the provider portal to continue (IdP-initiated).'
                            }
                          >
                            {spInitiated ? 'SSO login' : 'IdP initiated only'}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="org-note">
                    {isActive ? 'No SSO connector is configured for this organization.' : 'Awaiting invitation acceptance.'}
                  </p>
                )}
              </article>
            );
          })}
        </div>

        <footer className="auth-footer">
          <button type="button" className="auth-link-button" onClick={goToOrgCreate}>
            Create a new organization
          </button>
        </footer>
      </section>
    </main>
  );
};

export default OrgSelectPage;
